import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import JunoPage from "../../components/JunoPage/JunoPage";
import HierarchyChartView from "../../components/HierarchyChartView/HierarchyChartView";
import HierarchyTreeView from "../../components/HierarchyTreeView/HierarchyTreeView";
import { Location } from "fhir/r5";
import { Loader, Title } from "@fyrstain/hl7-front-library";
import { Badge, Button, Card, Form, InputGroup } from "react-bootstrap";
import i18n from "i18next";
import LocationService from "../../services/LocationService";
import { useErrorNavigation } from "../../services/useErrorNavigation";
import {
  buildLocationTree,
  countLocationLeaves,
  countLocationNodes,
  countLocationsByLevel,
  filterLocationTree,
  getLocationDisplayName,
  getLocationKey,
  getLocationSearchTerms,
  normalizeLocationSearchValue,
} from "../../utils/locationTree";
import "../../components/HierarchyView/HierarchyView.css";

const LocationStructure: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const navigate = useNavigate();

  //////////////////////////////
  //           State          //
  //////////////////////////////

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [collapsedNodes, setCollapsedNodes] = useState<Record<string, boolean>>(
    {}
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"tree" | "chart">("tree");

  const onError = useErrorNavigation();

  //////////////////////////////
  //      Data loading        //
  //////////////////////////////

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await LocationService.loadAllInternalLocations();
      setLocations(response);
    } catch (error) {
      onError(error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  //////////////////////////////
  //     Tree data            //
  //////////////////////////////

  const tree = useMemo(() => buildLocationTree(locations), [locations]);
  const normalizedSearchTerm = useMemo(
    () => normalizeLocationSearchValue(searchTerm.trim()),
    [searchTerm]
  );
  const filteredTree = useMemo(
    () =>
      filterLocationTree(tree, normalizedSearchTerm),
    [tree, normalizedSearchTerm]
  );
  const totalLocations = locations.length;
  const totalRoots = filteredTree.nodes.length;
  const totalLeaves = countLocationLeaves(filteredTree.nodes);
  const treeCount = countLocationNodes(filteredTree.nodes);
  const locationsByLevel = useMemo(
    () => countLocationsByLevel(filteredTree.nodes),
    [filteredTree.nodes]
  );
  const hasSearch = normalizedSearchTerm.length > 0;
  const visibleMatches = hasSearch ? filteredTree.matches : totalLocations;
  const isChartView = viewMode === "chart";

  //////////////////////////////
  //          Actions         //
  //////////////////////////////

  const toggleNode = useCallback((key: string) => {
    setCollapsedNodes((current) => ({
      ...current,
      [key]: !(current[key] ?? true),
    }));
  }, []);

  const goToLocationDetails = useCallback(
    (locationId?: string | null) => {
      if (locationId) {
        navigate(`/Location/${encodeURIComponent(locationId)}`);
      }
    },
    [navigate]
  );

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage
      titleKey={i18n.t("title.locationstructure")}
      loading={loading && !isChartView}
      needsLogin={true}
    >
      <div className="section">
        <Card className="card">
          <Card.Header>
            <Title
              level={2}
              prefix="Structure"
              content={i18n.t("title.locationstructure")}
            />
          </Card.Header>
          <Card.Body>
            <div className="hierarchy-view-switch">
              <Button
                variant={isChartView ? "outline-secondary" : "secondary"}
                onClick={() => setViewMode("tree")}
              >
                {i18n.t("label.treeview")}
              </Button>
              <Button
                variant={isChartView ? "secondary" : "outline-secondary"}
                onClick={() => setViewMode("chart")}
              >
                {i18n.t("label.chartview")}
              </Button>
            </div>

            {isChartView ? (
              <HierarchyChartView<Location>
                items={locations}
                loading={loading}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                buildTree={buildLocationTree}
                getKey={getLocationKey}
                getDisplayName={getLocationDisplayName}
                getSearchTerms={getLocationSearchTerms}
                onGoToDetails={goToLocationDetails}
                labels={{
                  searchPlaceholder: i18n.t("label.locationsearch"),
                  reset: i18n.t("button.reset"),
                  center: i18n.t("button.centerview"),
                  expandAll: i18n.t("button.expandall"),
                  collapseAll: i18n.t("button.collapseall"),
                  root: i18n.t("label.root"),
                  level: i18n.t("label.level"),
                  childItems: i18n.t("label.sublocations"),
                  leaf: i18n.t("label.leaf"),
                  searchResults: i18n.t("label.searchresults"),
                  structureEmpty: i18n.t("title.locationstructureempty"),
                  structureNoResults: i18n.t("title.locationstructurenoresults"),
                  open: i18n.t("label.openlocation"),
                  close: i18n.t("label.closelocation"),
                  details: i18n.t("label.details"),
                  totalItemsLabel: i18n.t("label.locations"),
                  rootItemsLabel: i18n.t("label.rootlocations"),
                  leafItemsLabel: i18n.t("label.leaflocations"),
                }}
              />
            ) : (
              <>
                <div className="hierarchy-search">
                  <InputGroup>
                    <Form.Control
                      type="search"
                      value={searchTerm}
                      placeholder={i18n.t("label.locationsearch")}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSearchTerm("")}
                      disabled={!searchTerm}
                    >
                      {i18n.t("button.reset")}
                    </Button>
                  </InputGroup>
                </div>
                <div className="d-flex flex-wrap gap-3 mb-4">
                  <Badge bg="secondary">
                    {totalLocations} {i18n.t("label.locations")}
                  </Badge>
                  <Badge bg="info">
                    {totalRoots} {i18n.t("label.rootlocations")}
                  </Badge>
                  <Badge bg="success">
                    {totalLeaves} {i18n.t("label.leaflocations")}
                  </Badge>
                  {hasSearch && (
                    <Badge bg="primary">
                      {visibleMatches} {i18n.t("label.searchresults")}
                    </Badge>
                  )}
                  {locationsByLevel.map((count, index) => (
                    <Badge key={`level-${index}`} bg={index % 2 === 0 ? "dark" : "secondary"}>
                      {i18n.t("label.level")} {index + 1}: {count}
                    </Badge>
                  ))}
                </div>
                {loading ? (
                  <div className="displayFlexCenter py-5">
                    <Loader />
                  </div>
                ) : treeCount > 0 ? (
                  <HierarchyTreeView<Location>
                    filteredTree={filteredTree}
                    collapsedNodes={collapsedNodes}
                    normalizedSearchTerm={normalizedSearchTerm}
                    onToggleNode={toggleNode}
                    onGoToDetails={goToLocationDetails}
                    getKey={getLocationKey}
                    getDisplayName={getLocationDisplayName}
                    labels={{
                      open: i18n.t("label.openlocation"),
                      close: i18n.t("label.closelocation"),
                      details: i18n.t("label.details"),
                      root: i18n.t("label.root"),
                      level: i18n.t("label.level"),
                      subItems: i18n.t("label.sublocations"),
                      leaf: i18n.t("label.leaf"),
                      identifier: i18n.t("label.identifier"),
                      parentIdentifier: i18n.t("label.parentlocation"),
                      rootValue: "ROOT",
                    }}
                  />
                ) : (
                  <div>
                    {hasSearch
                      ? i18n.t("title.locationstructurenoresults")
                      : i18n.t("title.locationstructureempty")}
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </JunoPage>
  );
};

export default LocationStructure;
